(function () {
    "use strict";

    const SELECTOR_BTN = ".js-sample-button";
    const MODAL_ROOT_ID = "SamplesModalRoot";
    const SAMPLE_LIMIT_SAMPLES_ONLY = 4;

    const state = {
        cart: null,
        derived: null,
        isBusy: false,
        lastOpener: null,
        modalOpen: false,
        lastAddedVariantId: null,
        lastModalMessageType: "list",
    };

    function getRoutes() {
        const routes = (window.theme && window.theme.routes) || {};
        return {
            cartAdd: routes.cartAdd || "/cart/add.js",
            cartChange: routes.cartChange || "/cart/change.js",
            cart: routes.cart || "/cart",
            cartJs: "/cart.js",
            checkout: "/checkout",
        };
    }

    function getSampleSettings() {
        const settings = (window.theme && window.theme.settings) || {};
        return {
            modalTitle: settings.samples_modal_title || "My Samples",
            subtitleSamplesOnly: settings.samples_subtitle_samples_only || "Add up to 4 samples",
            subtitleUnlimited: settings.samples_subtitle_unlimited || "Unlimited samples (full product in cart)",
            limitOverlayTitle: settings.samples_limit_overlay_title || "FULL",
            limitNotice: settings.samples_limit_notice || "Your samples are full. Remove one to add another.",
            shippingNote: settings.samples_shipping_note || "We'll post your samples First Class",
            primaryButton: settings.samples_primary_button || "Order My Free Samples",
            addButtonText: settings.samples_add_button_text || "Add Sample",
            confirmationText: settings.samples_confirmation_text || "Are you sure you want to remove this sample?"
        };
    }

    function escapeHtml(str) {
        return String(str ?? "")
            .replaceAll("&", "&amp;")
            .replaceAll("<", "&lt;")
            .replaceAll(">", "&gt;")
            .replaceAll('"', "&quot;")
            .replaceAll("'", "&#039;");
    }

    function isSampleItem(item) {
        const props = item && item.properties;
        return !!(
            props &&
            (props._is_sample === "1" || props._is_sample === 1 || props._is_sample === true)
        );
    }

    function buildDerived(cart) {
        const items = (cart && cart.items) || [];
        const sampleItems = [];
        const nonSampleItems = [];

        for (const item of items) {
            if (isSampleItem(item)) sampleItems.push(item);
            else nonSampleItems.push(item);
        }

        const hasNonSample = nonSampleItems.length > 0;
        const sampleCount = sampleItems.length;

        const sampleVariantIds = new Set();
        for (const item of sampleItems) {
            if (item.variant_id) sampleVariantIds.add(String(item.variant_id));
        }

        return {
            items,
            sampleItems,
            nonSampleItems,
            hasNonSample,
            sampleCount,
            sampleVariantIds,
            maxSamples: hasNonSample ? Infinity : SAMPLE_LIMIT_SAMPLES_ONLY,
            remaining: hasNonSample ? Infinity : Math.max(0, SAMPLE_LIMIT_SAMPLES_ONLY - sampleCount),
        };
    }

    async function fetchCart() {
        const { cartJs } = getRoutes();
        const res = await fetch(cartJs, {
            method: "GET",
            credentials: "same-origin",
            headers: { Accept: "application/json" },
        });
        if (!res.ok) throw new Error("Failed to fetch cart");
        return res.json();
    }

    async function refreshState({ silent } = { silent: false }) {
        try {
            const cart = await fetchCart();
            state.cart = cart;
            state.derived = buildDerived(cart);
            if (!silent) {
                updateAllButtons();
                if (state.modalOpen) renderModal({ messageType: state.lastModalMessageType });
            }
            return state;
        } catch (e) {
            return state;
        }
    }

    function canAddSample() {
        const d = state.derived || buildDerived(state.cart || { items: [] });
        if (d.hasNonSample) return { ok: true };
        if (d.sampleCount < SAMPLE_LIMIT_SAMPLES_ONLY) return { ok: true };
        return { ok: false, reason: "limit" };
    }

    function setButtonState(btn, { added, loading }) {
        if (!btn) return;
        btn.classList.toggle("is-added", !!added);
        btn.classList.toggle("is-loading", !!loading);

        const icon = btn.querySelector(".sample-button__icon");
        const text = btn.querySelector(".sample-button__text");
        const settings = getSampleSettings();

        if (loading) {
            if (text) text.textContent = "Adding…";
            if (icon) icon.textContent = "…";
            return;
        }

        if (added) {
            if (text) text.textContent = "Added";
            if (icon) icon.textContent = "✓";
        } else {
            if (text) text.textContent = settings.addButtonText;
            if (icon) icon.textContent = "+";
        }
    }

    function updateAllButtons() {
        const buttons = document.querySelectorAll(SELECTOR_BTN);
        const d = state.derived;
        if (!buttons || !buttons.length) return;

        for (const btn of buttons) {
            const vId = btn.getAttribute("data-sample-variant-id");
            const added = d ? d.sampleVariantIds.has(String(vId)) : false;
            const loading = state.isBusy && state.lastOpener === btn;
            setButtonState(btn, { added, loading });
        }
    }

    function refreshEnterpriseCartDataOnly() {
        document.dispatchEvent(new CustomEvent("dispatch:cart-drawer:refresh"));
    }

    async function addSample(variantId, parentHandle, openerBtn) {
        if (!variantId) return;
        if (state.isBusy) return;

        state.isBusy = true;
        state.lastOpener = openerBtn || null;
        updateAllButtons();

        try {
            await refreshState({ silent: true });

            const gate = canAddSample();
            if (!gate.ok) {
                state.isBusy = false;
                state.lastOpener = null;
                updateAllButtons();
                openModal({ type: "limit" });
                return;
            }

            const { cartAdd } = getRoutes();

            const payload = {
                items: [
                    {
                        id: Number(variantId),
                        quantity: 1,
                        properties: {
                            _is_sample: "1",
                            _sample_of: parentHandle ? String(parentHandle) : "",
                        },
                    },
                ],
            };

            const res = await fetch(cartAdd, {
                method: "POST",
                credentials: "same-origin",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                body: JSON.stringify(payload),
            });

            if (!res.ok) throw new Error("Add sample failed");

            refreshEnterpriseCartDataOnly();
            await refreshState({ silent: true });

            state.lastAddedVariantId = String(variantId);

            state.isBusy = false;
            state.lastOpener = null;
            updateAllButtons();

            openModal({ type: "list" });
        } catch (e) {
            state.isBusy = false;
            state.lastOpener = null;
            updateAllButtons();
            openModal({ type: "error" });
        }
    }

    async function removeLineItemByKey(lineKey) {
        if (!lineKey) return { ok: false };
        if (state.isBusy) return { ok: false };

        state.isBusy = true;
        updateAllButtons();

        try {
            const { cartChange } = getRoutes();
            const payload = { id: String(lineKey), quantity: 0 };

            const res = await fetch(cartChange, {
                method: "POST",
                credentials: "same-origin",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                body: JSON.stringify(payload),
            });

            if (!res.ok) throw new Error("Remove failed");

            refreshEnterpriseCartDataOnly();
            await refreshState({ silent: true });

            state.isBusy = false;
            updateAllButtons();

            return { ok: true };
        } catch (e) {
            state.isBusy = false;
            updateAllButtons();
            return { ok: false };
        }
    }

    function ensureModalRoot() {
        let root = document.getElementById(MODAL_ROOT_ID);
        if (!root) {
            root = document.createElement("div");
            root.id = MODAL_ROOT_ID;
            document.body.appendChild(root);
        }
        return root;
    }

    function closeModal() {
        state.modalOpen = false;

        const root = ensureModalRoot();
        const ui = root.querySelector(".samples-ui");
        document.documentElement.classList.remove("samples-modal-open");

        if (!ui) {
            root.innerHTML = "";
            return;
        }

        ui.classList.remove("is-open");

        const cleanup = () => {
            root.innerHTML = "";
        };

        let done = false;
        const onEnd = (e) => {
            if (done) return;
            if (e && e.target !== ui) return;
            done = true;
            ui.removeEventListener("transitionend", onEnd);
            cleanup();
        };

        ui.addEventListener("transitionend", onEnd);
        window.setTimeout(() => {
            if (done) return;
            done = true;
            ui.removeEventListener("transitionend", onEnd);
            cleanup();
        }, 350);
    }

    function forceKeepModalOpen() {
        const root = ensureModalRoot();
        const ui = root.querySelector(".samples-ui");
        if (!ui) return;
        ui.classList.add("is-open");
        state.modalOpen = true;
        document.documentElement.classList.add("samples-modal-open");
    }

    function openModal({ type } = { type: "list" }) {
        state.modalOpen = true;
        state.lastModalMessageType = type || "list";
        renderModal({ messageType: state.lastModalMessageType });
        document.documentElement.classList.add("samples-modal-open");

        const root = ensureModalRoot();
        const ui = root.querySelector(".samples-ui");
        if (!ui) return;

        ui.classList.remove("is-open");
        ui.offsetHeight;
        requestAnimationFrame(() => {
            ui.classList.add("is-open");
        });
    }

    function getModalHeaderText(d, messageType) {
        const settings = getSampleSettings();

        if (messageType === "error") {
            return { title: settings.modalTitle, subtitle: "Something went wrong. Please try again." };
        }

        if (d.hasNonSample) {
            return { title: settings.modalTitle, subtitle: settings.subtitleUnlimited };
        }

        return { title: settings.modalTitle, subtitle: settings.subtitleSamplesOnly };
    }

    function renderModal({ messageType } = { messageType: "list" }) {
        state.lastModalMessageType = messageType || "list";

        const root = ensureModalRoot();
        const d = state.derived || buildDerived(state.cart || { items: [] });
        const { cart } = getRoutes();
        const settings = getSampleSettings();

        const head = getModalHeaderText(d, messageType);

        const noticeLine =
            messageType === "limit"
                ? settings.limitNotice
                : settings.shippingNote;

        const maxSlots = d.hasNonSample ? Math.max(d.sampleCount, 4) : SAMPLE_LIMIT_SAMPLES_ONLY;

        const showLimitPopup = messageType === "limit";
        const limitPopupHtml = `
      <div class="samples-ui__limit-overlay" aria-hidden="${showLimitPopup ? "false" : "true"}">
        <div class="samples-ui__limit-card" role="dialog" aria-modal="true" aria-label="Samples limit reached">
          <button type="button" class="samples-ui__limit-close" data-limit-close aria-label="Close">×</button>
          <div class="samples-ui__limit-title">${escapeHtml(settings.limitOverlayTitle)}</div>
          <div class="samples-ui__limit-text">${escapeHtml(settings.limitNotice)}</div>
        </div>
      </div>
    `;

        const slotsHtml = [];
        for (let i = 0; i < maxSlots; i++) {
            const item = d.sampleItems[i];
            if (!item) {
                slotsHtml.push(`
          <div class="samples-ui__slot samples-ui__slot--empty">
            <div class="samples-ui__thumb samples-ui__thumb--empty"></div>
            <div class="samples-ui__empty-text">Empty Space</div>
          </div>
        `);
                continue;
            }

            const img = item.image
                ? `<img class="samples-ui__thumb" src="${escapeHtml(item.image)}" alt="${escapeHtml(
                    item.product_title || item.title
                )}" loading="lazy">`
                : `<div class="samples-ui__thumb samples-ui__thumb--empty"></div>`;

            const name = escapeHtml(item.product_title || item.title || "Sample");
            const key = escapeHtml(item.key);

            const justAdded =
                state.lastAddedVariantId && String(item.variant_id) === String(state.lastAddedVariantId);

            slotsHtml.push(`
        <div class="samples-ui__slot">
          <div class="samples-ui__slot-inner">
            ${img}
            <div class="samples-ui__name">${name}</div>
            <button type="button" class="samples-ui__remove" data-remove-key="${key}" aria-label="Remove">
                <svg width="20" height="20" viewBox="0 0 16 16" aria-hidden="true" focusable="false" role="presentation" class="icon"><path d="M14 3h-3.53a3.07 3.07 0 0 0-.6-1.65C9.44.82 8.8.5 8 .5s-1.44.32-1.87.85A3.06 3.06 0 0 0 5.53 3H2a.5.5 0 0 0 0 1h1.25v10c0 .28.22.5.5.5h8.5a.5.5 0 0 0 .5-.5V4H14a.5.5 0 0 0 0-1zM6.91 1.98c.23-.29.58-.48 1.09-.48s.85.19 1.09.48c.2.24.3.6.36 1.02h-2.9c.05-.42.17-.78.36-1.02zm4.84 11.52h-7.5V4h7.5v9.5z" fill="currentColor"></path><path d="M6.55 5.25a.5.5 0 0 0-.5.5v6a.5.5 0 0 0 1 0v-6a.5.5 0 0 0-.5-.5zm2.9 0a.5.5 0 0 0-.5.5v6a.5.5 0 0 0 1 0v-6a.5.5 0 0 0-.5-.5z" fill="currentColor"></path></svg>
            </button>
            ${justAdded ? `<div class="samples-ui__badge">ADDED</div>` : ``}
          </div>
        </div>
      `);
        }

        root.innerHTML = `
      <div class="samples-ui" role="dialog" aria-modal="true" aria-label="${escapeHtml(settings.modalTitle)}">
        <div class="samples-ui__overlay" data-samples-close></div>
        <div class="samples-ui__panel">
          <div class="samples-ui__busy"><div class="samples-ui__spinner" aria-hidden="true"></div></div>

          <div class="samples-ui__confirm" aria-hidden="true">
            <div class="samples-ui__confirm-card" role="dialog" aria-modal="true" aria-label="Remove sample confirmation">
              <div class="samples-ui__confirm-title">Remove?</div>
              <div class="samples-ui__confirm-text">${escapeHtml(settings.confirmationText)}</div>
              <div class="samples-ui__confirm-actions">
                <button type="button" class="samples-ui__confirm-btn samples-ui__confirm-btn--yes" data-confirm-yes>Yes</button>
                <button type="button" class="samples-ui__confirm-btn samples-ui__confirm-btn--no" data-confirm-no>No</button>
              </div>
            </div>
          </div>

          ${limitPopupHtml}

          <button type="button" class="samples-ui__close" data-samples-close aria-label="Close">×</button>

          <div class="samples-ui__header">
            <div class="samples-ui__title">${escapeHtml(head.title)}</div>
            <div class="samples-ui__subtitle">${escapeHtml(head.subtitle)}</div>
            <div class="samples-ui__note">${escapeHtml(noticeLine)}</div>
          </div>

          <div class="samples-ui__grid">
            ${slotsHtml.join("")}
          </div>

          <div class="samples-ui__actions">
            <a class="samples-ui__btn samples-ui__btn--primary" href="${escapeHtml(cart)}">${escapeHtml(settings.primaryButton)}</a>
            <button type="button" class="samples-ui__btn samples-ui__btn--secondary" data-samples-close>Continue Shopping</button>
          </div>
        </div>
      </div>
    `;

        root.querySelectorAll("[data-samples-close]").forEach((el) => {
            el.addEventListener("click", (e) => {
                e.preventDefault();
                closeModal();
            });
        });

        const ui = root.querySelector(".samples-ui");
        if (ui) {
            ui.classList.toggle("has-limit", messageType === "limit");
        }

        root.querySelector("[data-limit-close]")?.addEventListener("click", (e) => {
            e.preventDefault();
            if (ui) ui.classList.remove("has-limit");
            // keep modal open
            forceKeepModalOpen();
        });

        let pendingRemoveKey = null;

        root.querySelectorAll("[data-remove-key]").forEach((btn) => {
            btn.addEventListener("click", (e) => {
                e.preventDefault();
                pendingRemoveKey = btn.getAttribute("data-remove-key");
                if (ui) ui.classList.add("has-confirm");
            });
        });

        root.querySelector("[data-confirm-no]")?.addEventListener("click", (e) => {
            e.preventDefault();
            pendingRemoveKey = null;
            if (ui) ui.classList.remove("has-confirm");
            forceKeepModalOpen();
        });

        root.querySelector("[data-confirm-yes]")?.addEventListener("click", async (e) => {
            e.preventDefault();
            const key = pendingRemoveKey;
            pendingRemoveKey = null;

            if (!key) return;

            if (ui) {
                ui.classList.remove("has-confirm");
                ui.classList.add("is-busy");
            }

            const slot = root
                .querySelector(`[data-remove-key="${CSS.escape(key)}"]`)
                ?.closest(".samples-ui__slot");
            if (slot) slot.classList.add("is-removing");

            const result = await removeLineItemByKey(key);

            // important: keep modal open, re-render list
            if (result.ok) {
                renderModal({ messageType: "list" });
                forceKeepModalOpen();
            } else {
                renderModal({ messageType: "error" });
                forceKeepModalOpen();
            }

            const newUi = ensureModalRoot().querySelector(".samples-ui");
            if (newUi) newUi.classList.remove("is-busy");
        });

        window.addEventListener(
            "keydown",
            (e) => {
                if (e.key === "Escape" && state.modalOpen) closeModal();
            },
            { once: true }
        );
    }

    function onClickSampleButton(e) {
        const btn = e.target && e.target.closest ? e.target.closest(SELECTOR_BTN) : null;
        if (!btn) return;

        e.preventDefault();

        const variantId = btn.getAttribute("data-sample-variant-id");
        const parentHandle = btn.getAttribute("data-parent-handle");

        const d = state.derived;
        const isAdded = d ? d.sampleVariantIds.has(String(variantId)) : false;

        if (isAdded) {
            openModal({ type: "list" });
            return;
        }

        addSample(variantId, parentHandle, btn);
    }

    function bindGlobalListeners() {
        document.addEventListener("click", onClickSampleButton);

        document.addEventListener("on:line-item:change", async () => {
            await refreshState();
        });

        document.addEventListener("samples:refresh", async () => {
            await refreshState();
        });
    }

    async function init() {
        bindGlobalListeners();
        await refreshState();
    }

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", init, { once: true });
    } else {
        init();
    }

    window.Samples = {
        refresh: () => refreshState(),
        open: () => openModal({ type: "list" }),
        close: () => closeModal(),
    };
})();
